<template>
    <auth-layout id="register-page" class="h-100">
        <div class="col-12 col-lg-8 col-xl-6">
            <div class="row pt-4 pb-5">
                <div class="col-12">
                    <h1>{{ $t('reg.title')}}</h1>
                </div>
            </div>

            <auth-closed-shop-info-box></auth-closed-shop-info-box>

            <section id="login-form" v-if="!isB2b || (isB2b && (driverInvitationToken || fleetmanagerInvitationToken))">
                <div class="row pb-5">
                    <div class="col-12 col-md-6">
                        <b-form-group :label="$t('cdb.profile.form.email.label')" label-for="email">
                            <input :disabled="driverInvitationToken || fleetmanagerInvitationToken" id="email" class="form-control" :placeholder="$t('cdb.profile.form.email_repeat.filler')" type="email" v-model="email"/>
                            <div v-if="error.email" class="text-danger py-1">{{ $t('register.error.' + error.email[0]) }}</div>
                        </b-form-group>
                    </div>
                    <div class="col-12 col-md-6">
                        <b-form-group
                            :label="$t('cdb.profile.form.email_repeat.label')"
                            label-for="email"
                        >
                            <input :disabled="driverInvitationToken || fleetmanagerInvitationToken" id="email_confirmation" class="form-control" :placeholder="$t('cdb.profile.form.email.filler')" type="email" v-model="email_confirmation"/>
                        </b-form-group>
                    </div>
                </div>
                <div class="row pb-5">
                    <div class="col-12 col-md-6">
                        <b-form-group :label="$t('cdb.profile.form.password.label')" label-for="password">
                            <input id="password" class="form-control" :placeholder="$t('cdb.profile.form.password.filler')" type="password" v-model="password" />
                            <small>
                                {{ $t('reg.password.validation.title') }}
                                <ul>
                                    <li :class="{
                                        'text-danger': this.validation.password.length.state === false,
                                        'text-success': this.validation.password.length.state
                                    }">
                                        {{ $t('reg.password.validation.minimumCharacters') }}
                                    </li>
                                    <li :class="{
                                        'text-danger': this.validation.password.special.state === false,
                                        'text-success': this.validation.password.special.state
                                    }">
                                        {{$t('reg.password.validation.specialCharacter') }}
                                    </li>
                                </ul>
                            </small>
                        </b-form-group>
                    </div>
                    <div class="col-12 col-md-6">
                        <b-form-group :label="$t('cdb.profile.form.password_repeat.label')" label-for="password-repeat">
                            <input id="password-repeat" class="form-control" :placeholder="$t('cdb.profile.form.password.filler')"
                                    type="password" v-model="password_confirmation" v-on:keyup.enter="handleRegister"/>
                            <small :class="{
                                        'text-danger': this.validation.password_confirmation.equal.state === false,
                                        'text-success': this.validation.password_confirmation.equal.state
                            }">
                                {{ $t('reg.password.validation.mustBeSame') }}
                            </small>
                        </b-form-group>
                    </div>
                </div>
                <div class="row pb-5" v-if="referralCodeIsNecessary">
                    <div class="col-12">
                        <b-form-group :label="$t('cdb.profile.form.ref_code.label')" label-for="ref-code">
                            <input id="ref-code" class="form-control" type="text" v-model="refCode" />
                            <div v-if="validation.refCode.error" class="text-danger py-1">{{ validation.refCode.error }}</div>
                        </b-form-group>
                    </div>
                </div>
                <div class="row d-flex justify-content-end">
                    <div class="col-12 col-md-6 col-xl-4 order-xl-1">
                        <button type="button" class="btn btn-primary w-100" @click="handleRegister" v-if="!isLoading && inputIsValid">{{ $t('reg.submit') }}</button>
                        <button type="button" class="btn btn-primary w-100" v-if="!isLoading && !inputIsValid" disabled>{{ $t('reg.submit') }}</button>
                        <button type="button" class="btn btn-primary w-100 disabled" v-if="isLoading">{{ $t('co.button.loading')}}</button>
                    </div>

                    <div class="col-12 col-xl-8 d-flex justify-content-end pr-0">
                        <div class="d-flex justify-content-xl-end justify-content-center pt-2">
                            <div class="pr-2">{{ $t('reg.alreadyHaveAccount') }}</div>
                            <div class="pr-3"><b-link :to="$getUrl('login', null, { isInCheckout: this.$route.query.isInCheckout })">{{ $t('login.title') }}</b-link></div>
                        </div>
                    </div>
                </div>
            </section>
            <section v-else>
                <p>Sorry, you are not able to register in without an invitation.</p>
            </section>
        </div>
    </auth-layout>
</template>

<script>
import AuthLayout from "../../layouts/AuthLayout.vue";
import AuthClosedShopInfoBox from "../../components/molecules/AuthClosedShopInfoBox.vue";
import { BFormGroup, BLink } from "bootstrap-vue";

function debounce(func, wait) {
    let timeout;
    return function(...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

export default {
    components: {
        AuthLayout,
        BFormGroup, BLink,
        AuthClosedShopInfoBox
    },

    data() {
        return {
            email: '',
            email_confirmation: '',
            password: '',
            password_confirmation: '',
            error: '',
            errorTimer: '',
            isLoading: false,
            checksum: 0,
            driverInvitationToken: '',
            fleetmanagerInvitationToken: '',
            refCode: null,
            refCodeCheckTimer: null,
            validation: {
                refCode: {
                    state: null,
                    error: null,
                },
                password: {
                    length: {
                        state: null,
                    },
                    special: {
                        state: null,
                    },
                },
                password_confirmation: {
                    equal: {
                        state: null,
                    }
                }
            }
        }
    },
    computed: {
        inputIsValid() {
            return this.email && this.email_confirmation && this.validation.password.length.state && this.validation.password.special.state && this.validation.password_confirmation.equal.state && this.validation.refCode.state;
        },
        isB2b() {
            return this.$root.whitelabel?.state?.settings?.is_b2b
        },
        referralCodeIsNecessary() {
            return this.$root.whitelabel?.state?.settings?.uses_ref_codes && this.$root.whitelabel?.state?.settings?.requires_ref_code;
        }
    },
    async created() {
        this.driverInvitationToken = this.$route.query?.invitation
        this.fleetmanagerInvitationToken = this.$route.query?.invitation_fm

        if (!this.referralCodeIsNecessary) {
            this.validation.refCode.state = true;
        }

        if (this.driverInvitationToken) {
            const response = await this.$axios.get('api/driver-invitation', {
                params: {
                    invitation: this.driverInvitationToken
                }
            })

            this.email = response.data.email
            this.email_confirmation = response.data.email
        } else if (this.fleetmanagerInvitationToken) {
            const response = await this.$axios.get('api/fleetmanager-invitation', {
                params: {
                    invitation: this.fleetmanagerInvitationToken
                }
            })

            this.email = response.data.email
            this.email_confirmation = response.data.email
        }
    },
    watch: {
        password_confirmation(){
            this.validation.password_confirmation.equal.state = this.password === this.password_confirmation
        },
        password() {
            const specialCharacterCheck = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;

            this.validation.password.length.state = this.password.length >= 8
            this.validation.password.special.state = specialCharacterCheck.test(this.password)
        },
        refCode() {
            this.debouncedCheckCode();
        }

    },
    methods: {
        handleRegister() {
            this.isLoading = true
            let registerRoute = 'api/register'
            if (this.driverInvitationToken && !this.fleetmanagerInvitationToken) {
                registerRoute = 'api/register-driver'
            } else if (this.fleetmanagerInvitationToken && !this.driverInvitationToken) (
                registerRoute = 'api/register-fleetmanager'
            )

            this.$axios.get('/sanctum/csrf-cookie').then(() => {
                this.$axios.post(registerRoute, {
                    email: this.email,
                    email_confirmation: this.email_confirmation,
                    password: this.password,
                    password_confirmation: this.password_confirmation,
                    user_locale: this.$i18n.locale,
                    referral_code: this.refCode,
                })
                    .then(response => {
                        if (response.data.success) {
                            this.driverInvitationToken || this.fleetmanagerInvitationToken ? this.$goTo('register_init') : this.$goTo('account_not_verified')
                        } else {
                            this.error = response.data.message

                            clearTimeout(this.errorTimer);
                            this.errorTimer = setTimeout(() => {
                                this.error = '';
                            }, 10000);
                        }
                    })
                    .catch(error => {
                        this.isLoading = false
                        this.error = error.response.data.errors

                        clearTimeout(this.errorTimer);
                        this.errorTimer = setTimeout(() => {
                            this.error = '';
                        }, 10000);
                    });
            }).catch(error => {
                this.isLoading = false
                this.error = error.response.data.errors

                clearTimeout(this.errorTimer);
                this.errorTimer = setTimeout(() => {
                    this.error = '';
                }, 10000);
            });
        },


        checkCode() {
            this.$axios.get(`/api/v1/ref-code/check/${this.refCode}`).then(response => {
                this.validation.refCode.state = response.data.valid;
                if (!response.data.valid) {
                    this.validation.refCode.error = this.$t('reg.ref_code.error');
                } else {
                    this.validation.refCode.error = null;
                }
            });
        },

        debouncedCheckCode: debounce(function() {
            this.checkCode();
        }, 500),
    },
    head: {
        title: function () {
            return {
                inner: this.$root.whitelabel.state.settings.meta_title,
                complement: this.$t('meta.auth.register.title')
            }
        },
        meta: function () {
            return [
                { name: 'description', content: this.$t('meta.auth.register.description') }
            ]
        }
    }
}
</script>
